var _excluded = ["children", "backgroundStyle", "className", "banner", "hideCloseButton", "onClose", "scrollableTabIndex", "size"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useContext, Children, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keys, useEuiMemoizedStyles, useGeneratedHtmlId } from '../../services';
import { euiFlyoutChildStyles } from './flyout_child.styles';
import { EuiFlyoutCloseButton } from './_flyout_close_button';
import { EuiFlyoutContext } from './flyout_context';
import { EuiFlyoutBody } from './flyout_body';
import { EuiFocusTrap } from '../focus_trap';

/**
 * Props used to render and configure the child flyout panel
 */
import { jsx as ___EmotionJSX } from "@emotion/react";
/**
 * The child flyout is a panel that appears to the left of the parent flyout.
 * It is only visible when the parent flyout is open.
 */
export var EuiFlyoutChild = function EuiFlyoutChild(_ref) {
  var children = _ref.children,
    _ref$backgroundStyle = _ref.backgroundStyle,
    backgroundStyle = _ref$backgroundStyle === void 0 ? 'default' : _ref$backgroundStyle,
    className = _ref.className,
    banner = _ref.banner,
    _ref$hideCloseButton = _ref.hideCloseButton,
    hideCloseButton = _ref$hideCloseButton === void 0 ? false : _ref$hideCloseButton,
    onClose = _ref.onClose,
    _ref$scrollableTabInd = _ref.scrollableTabIndex,
    scrollableTabIndex = _ref$scrollableTabInd === void 0 ? 0 : _ref$scrollableTabInd,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 's' : _ref$size,
    rest = _objectWithoutProperties(_ref, _excluded);
  var flyoutContext = useContext(EuiFlyoutContext);
  if (!flyoutContext) {
    throw new Error('EuiFlyoutChild must be used as a child of EuiFlyout.');
  }
  var isChildFlyoutOpen = flyoutContext.isChildFlyoutOpen,
    setIsChildFlyoutOpen = flyoutContext.setIsChildFlyoutOpen,
    parentSize = flyoutContext.parentSize;
  useEffect(function () {
    setIsChildFlyoutOpen === null || setIsChildFlyoutOpen === void 0 || setIsChildFlyoutOpen(true);
    return function () {
      setIsChildFlyoutOpen === null || setIsChildFlyoutOpen === void 0 || setIsChildFlyoutOpen(false);
    };
  }, [setIsChildFlyoutOpen]);
  if (React.Children.count(children) === 0) {
    console.warn('EuiFlyoutChild was rendered with no children!');
  }
  if (parentSize === 'm' && size === 'm') {
    throw new Error('When the parent EuiFlyout size is "m", the EuiFlyoutChild size cannot be "m". Please use size "s" for the EuiFlyoutChild.');
  }
  var handleClose = function handleClose(event) {
    setIsChildFlyoutOpen === null || setIsChildFlyoutOpen === void 0 || setIsChildFlyoutOpen(false);
    onClose(event);
  };
  var flyoutTitleText;
  var hasDescribedByBody = false;
  Children.forEach(children, function (child) {
    if ( /*#__PURE__*/React.isValidElement(child)) {
      var _child$type;
      if (((_child$type = child.type) === null || _child$type === void 0 ? void 0 : _child$type.displayName) === 'EuiFlyoutHeader') {
        // Attempt to extract string content from header for ARIA
        var headerChildren = child.props.children;
        if (typeof headerChildren === 'string') {
          flyoutTitleText = headerChildren;
        } else if ( /*#__PURE__*/React.isValidElement(headerChildren) &&
        // Check if props exist and children is a string
        typeof headerChildren.props.children === 'string') {
          flyoutTitleText = headerChildren.props.children;
        } else if (Array.isArray(headerChildren)) {
          // Find the first string child if headerChildren is an array
          flyoutTitleText = headerChildren.find(function (cNode) {
            return typeof cNode === 'string';
          });
        }
      }
      if (child.type === EuiFlyoutBody) {
        hasDescribedByBody = true;
      }
    }
  });
  var titleIdGenerated = useGeneratedHtmlId({
    prefix: 'euiFlyoutChildTitle'
  });
  var bodyIdGenerated = useGeneratedHtmlId({
    prefix: 'euiFlyoutChildBody'
  });
  var ariaLabelledBy = flyoutTitleText ? titleIdGenerated : undefined;
  var ariaDescribedBy = hasDescribedByBody ? bodyIdGenerated : undefined;
  // Use existing aria-label if provided, otherwise fallback if no labelledby can be derived
  var ariaLabel = rest['aria-label'] || (!ariaLabelledBy && !flyoutTitleText ? 'Flyout panel' : undefined);
  var processedChildren = useMemo(function () {
    return Children.map(children, function (child) {
      if ( /*#__PURE__*/React.isValidElement(child)) {
        var _child$type2, _child$type3;
        if ((child.type === EuiFlyoutBody || ((_child$type2 = child.type) === null || _child$type2 === void 0 ? void 0 : _child$type2.displayName) === 'EuiFlyoutBody') && hasDescribedByBody) {
          return /*#__PURE__*/React.cloneElement(child, {
            id: bodyIdGenerated
          });
        }
        // If EuiFlyoutHeader is found and we derived flyoutTitleText, set its ID
        if (((_child$type3 = child.type) === null || _child$type3 === void 0 ? void 0 : _child$type3.displayName) === 'EuiFlyoutHeader' && flyoutTitleText && ariaLabelledBy) {
          return /*#__PURE__*/React.cloneElement(child, {
            id: titleIdGenerated
          });
        }
      }
      return child;
    });
  }, [children, bodyIdGenerated, titleIdGenerated, hasDescribedByBody, flyoutTitleText, ariaLabelledBy]);
  var flyoutWrapperRef = useRef(null);
  var classes = classNames('euiFlyoutChild', className);
  var styles = useEuiMemoizedStyles(euiFlyoutChildStyles);
  var childLayoutMode = flyoutContext.childLayoutMode,
    parentFlyoutRef = flyoutContext.parentFlyoutRef;
  var flyoutChildCss = [styles.euiFlyoutChild, backgroundStyle === 'shaded' ? styles.backgroundShaded : styles.backgroundDefault, size === 's' ? styles.s : styles.m, childLayoutMode === 'side-by-side' ? styles.sidePosition : styles.stackedPosition];
  var onKeyDown = useCallback(function (event) {
    if (isChildFlyoutOpen && event.key === keys.ESCAPE) {
      event.preventDefault();
      setIsChildFlyoutOpen === null || setIsChildFlyoutOpen === void 0 || setIsChildFlyoutOpen(false);
      onClose(event.nativeEvent);
    }
  }, [isChildFlyoutOpen, onClose, setIsChildFlyoutOpen]);
  return ___EmotionJSX(EuiFocusTrap, {
    returnFocus: function returnFocus() {
      if (parentFlyoutRef !== null && parentFlyoutRef !== void 0 && parentFlyoutRef.current) {
        parentFlyoutRef.current.focus();
        return false; // We've handled focus
      }
      return true;
    },
    shards: [],
    disabled: false
  }, ___EmotionJSX("div", _extends({
    ref: flyoutWrapperRef,
    className: classes,
    css: flyoutChildCss,
    "data-test-subj": "euiFlyoutChild",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    tabIndex: -1,
    onKeyDown: onKeyDown // used as generic container event handler
  }, rest), flyoutTitleText && !ariaLabelledBy && ___EmotionJSX("h2", {
    id: titleIdGenerated,
    className: "euiScreenReaderOnly"
  }, flyoutTitleText), !hideCloseButton && ___EmotionJSX(EuiFlyoutCloseButton, {
    className: "euiFlyoutChild__closeButton",
    onClose: handleClose,
    side: "right",
    closeButtonPosition: "inside",
    "data-test-subj": "euiFlyoutChildCloseButton"
  }), ___EmotionJSX("div", {
    tabIndex: scrollableTabIndex,
    className: "euiFlyoutChild__overflow",
    css: styles.overflow.overflow
  }, banner && ___EmotionJSX("div", {
    className: "euiFlyoutChild__banner",
    css: styles.banner,
    "data-test-subj": "euiFlyoutChildBanner"
  }, banner), ___EmotionJSX("div", {
    className: "euiFlyoutChild__overflowContent",
    css: styles.overflow.wrapper
  }, processedChildren))));
};
EuiFlyoutChild.propTypes = {
  /**
     * Called when the child panel's close button is clicked
     */
  onClose: PropTypes.func.isRequired,
  /**
     * Use to display a banner at the top of the child. It is suggested to use `EuiCallOut` for it.
     */
  banner: PropTypes.node,
  /**
     * Hides the default close button. You must provide another close button somewhere within the child flyout.
     * @default false
     */
  hideCloseButton: PropTypes.bool,
  /**
     * [Scrollable regions (or their children) should be focusable](https://dequeuniversity.com/rules/axe/4.0/scrollable-region-focusable)
     * to allow keyboard users to scroll the region via arrow keys.
     *
     * By default, EuiFlyoutChild's scroll overflow wrapper sets a `tabIndex` of `0`.
     * If you know your flyout child content already contains focusable children
     * that satisfy keyboard accessibility requirements, you can use this prop
     * to override this default.
     */
  scrollableTabIndex: PropTypes.number,
  /**
     * Size of the child flyout panel.
     * When the parent flyout is 'm', child is limited to 's'.
     * @default 's'
     */
  size: PropTypes.oneOf(["s", "m"]),
  /*
     * The background of the child flyout can be optionally shaded. Use `shaded` to add the shading.
     */
  backgroundStyle: PropTypes.oneOf(["shaded", "default"]),
  /**
     * Children are implicitly part of FunctionComponent, but good to have if props type is standalone.
     */
  children: PropTypes.node,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
EuiFlyoutChild.displayName = 'EuiFlyoutChild';